import axiosHandler from '@/utils/axios-handler';
import { AxiosError, AxiosResponse } from 'axios';
import { QueryClient } from '@tanstack/react-query';
import { academyApiUrl } from '@/lib/constants/api';
import { LEARNER_QUERY_KEY } from '@/lib/constants/query-keys';

interface UserActivity {
  isBookmarked?: boolean;
  isSubscribed?: boolean;
  isLiked?: boolean;
  progress?: {
    hasStarted?: boolean;
    isCompleted?: boolean;
    percentage?: number;
    isLearningMaterialCompleted?: boolean;
  } | null;
}

interface UserActivityCourseLesson {
  courseEntryUid: string | undefined;
  courseActivity?: UserActivity | null;

  lessonEntryUid?: string | undefined;
  lessonActivity?: UserActivity | null;
  queryClient?: QueryClient;
}

interface UserActivityLearningPath {
  learningPathUid: string | undefined;
  learningPathActivity: UserActivity | null;
  queryClient?: QueryClient;
}

/** Updates learning-path activity (enroll, start, bookmark, etc.) via a PUT to the Academy API. */
export const handleLearningPath = async ({
  learningPathUid,
  learningPathActivity,
  queryClient,
}: UserActivityLearningPath): Promise<AxiosResponse> => {
  try {
    const response = await axiosHandler({
      method: 'PUT',
      url: academyApiUrl(`/v1/users/learning-paths/${learningPathUid}`),
      withCredentials: true,
      data: learningPathActivity,
    });
    
    if (queryClient && learningPathUid) {
      await queryClient.invalidateQueries({
        queryKey: ['learningPath', 'progress', learningPathUid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learning-path-user-data', learningPathUid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['user-learning-paths-enrolled'],
      });
      await queryClient.invalidateQueries({
        // Every /api/learner list shows bookmark state.
        queryKey: [LEARNER_QUERY_KEY],
      });
    }
    
    return response as AxiosResponse;
  } catch (error: unknown) {
    if ((error as AxiosError).response) {
      return (error as AxiosError).response as AxiosResponse;
    } else {
      throw error;
    }
  }
};

export const handleCourse = async ({
  courseEntryUid,
  courseActivity,
  queryClient,
}: UserActivityCourseLesson): Promise<AxiosResponse> => {
  try {
    const response = await axiosHandler({
      method: 'PUT',
      url: academyApiUrl(`/v1/users/courses/${courseEntryUid}`),
      withCredentials: true,
      data: courseActivity,
    });
    
    if (queryClient && courseEntryUid) {
      await queryClient.invalidateQueries({
        queryKey: ['user-course-lessons', courseEntryUid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learningPath', 'lessonProgress', courseEntryUid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learningPath', 'progress'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learning-path-user-data'],
      });
      await queryClient.invalidateQueries({
        // Every /api/learner list shows bookmark state.
        queryKey: [LEARNER_QUERY_KEY],
      });
    }
    
    return response as AxiosResponse;
  } catch (error: unknown) {
    if ((error as AxiosError).response) {
      return (error as AxiosError).response as AxiosResponse;
    } else {
      throw error;
    }
  }
};

export const handleLesson = async ({
  courseEntryUid,
  lessonEntryUid,
  lessonActivity,
  queryClient,
}: UserActivityCourseLesson): Promise<AxiosResponse> => {
  try {
    const response = await axiosHandler({
      method: 'PUT',
      url: academyApiUrl(`/v1/users/courses/${courseEntryUid}/lessons/${lessonEntryUid}`),
      withCredentials: true,
      data: lessonActivity,
    });
    
    if (queryClient && courseEntryUid) {
      await queryClient.invalidateQueries({
        queryKey: ['user-course-lessons', courseEntryUid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learningPath', 'lessonProgress', courseEntryUid],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learningPath', 'progress'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['learning-path-user-data'],
      });
      await queryClient.invalidateQueries({
        // Every /api/learner list shows bookmark state.
        queryKey: [LEARNER_QUERY_KEY],
      });
    }
    
    return response as AxiosResponse;
  } catch (error: unknown) {
    if ((error as AxiosError).response) {
      return (error as AxiosError).response as AxiosResponse;
    } else {
      throw error;
    }
  }
};

export default async function updateUserActivity({
  isLessonVideo,
  lessonEntryUid,
  lessonActivity,
  courseEntryUid,
  courseActivity,
  queryClient,
}: {
  isLessonVideo: boolean;
  lessonEntryUid: string | undefined;
  lessonActivity: UserActivity | null;
  courseEntryUid: string | undefined;
  courseActivity: UserActivity | null;
  queryClient?: QueryClient;
}) {
  if (isLessonVideo) {
    return await handleLesson({
      courseEntryUid,
      lessonEntryUid,
      lessonActivity,
      queryClient,
    });
  } else {
    return await handleCourse({
      courseEntryUid,
      courseActivity,
      queryClient,
    });
  }
}
